import * as React from 'react'
import clsx from 'clsx'
import CloseIcon from '../internal/svg-icons/Close'
import { useForkRef } from '../../hooks/useForkRef'
import { SxProps, sx as sxFunc } from '../sx'
import { ButtonBase } from '../ButtonBase'
import { capitalize } from '../../utils'
import { AvatarProps } from '../Avatar'
import { SvgIconProps } from '../SvgIcon'
import styles from './index.module.scss'

export interface ChipClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `size="exSmall"`. */
  sizeExSmall: string
  /** Styles applied to the root element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the root element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the root element if `size="large"`. */
  sizeLarge: string
  /** Styles applied to the root element if `color="error"`. */
  colorError: string
  /** Styles applied to the root element if `color="info"`. */
  colorInfo: string
  /** Styles applied to the root element if `color="primary"`. */
  colorPrimary: string
  /** Styles applied to the root element if `color="secondary"`. */
  colorSecondary: string
  /** Styles applied to the root element if `color="success"`. */
  colorSuccess: string
  /** Styles applied to the root element if `color="warning"`. */
  colorWarning: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** Styles applied to the root element if `onClick` is defined or `clickable={true}`. */
  clickable: string
  /** Styles applied to the root element if `onClick` and `color="primary"` is defined or `clickable={true}`. */
  clickableColorPrimary: string
  /** Styles applied to the root element if `onClick` and `color="secondary"` is defined or `clickable={true}`. */
  clickableColorSecondary: string
  /** Styles applied to the root element if `onDelete` is defined. */
  deletable: string
  /** Styles applied to the root element if `onDelete` and `color="primary"` is defined. */
  deletableColorPrimary: string
  /** Styles applied to the root element if `onDelete` and `color="secondary"` is defined. */
  deletableColorSecondary: string
  /** Styles applied to the root element if `variant="outlined"`. */
  outlined: string
  /** Styles applied to the root element if `variant="filled"`. */
  filled: string
  /** Styles applied to the root element if `variant="outlined"` and `color="primary"`. */
  outlinedPrimary: string
  /** Styles applied to the root element if `variant="outlined"` and `color="secondary"`. */
  outlinedSecondary: string
  /** Styles applied to the root element if `variant="filled"` and `color="primary"`. */
  filledPrimary: string
  /** Styles applied to the root element if `variant="filled"` and `color="secondary"`. */
  filledSecondary: string
  /** Styles applied to the avatar element. */
  avatar: string
  /** Styles applied to the avatar element if `size="exSmall"`. */
  avatarExSmall: string
  /** Styles applied to the avatar element if `size="small"`. */
  avatarSmall: string
  /** Styles applied to the avatar element if `size="medium"`. */
  avatarMedium: string
  /** Styles applied to the avatar element if `size="large"`. */
  avatarLarge: string
  /** Styles applied to the avatar element if `color="primary"`. */
  avatarColorPrimary: string
  /** Styles applied to the avatar element if `color="secondary"`. */
  avatarColorSecondary: string
  /** Styles applied to the icon element. */
  icon: string
  /** Styles applied to the icon element if `size="exSmall"`. */
  iconExSmall: string
  /** Styles applied to the icon element if `size="small"`. */
  iconSmall: string
  /** Styles applied to the icon element if `size="medium"`. */
  iconMedium: string
  /** Styles applied to the icon element if `size="large"`. */
  iconLarge: string
  /** Styles applied to the icon element if `color="primary"`. */
  iconColorPrimary: string
  /** Styles applied to the icon element if `color="secondary"`. */
  iconColorSecondary: string
  /** Styles applied to the label `span` element. */
  label: string
  /** Styles applied to the label `span` element if `size="exSmall"`. */
  labelExSmall: string
  /** Styles applied to the label `span` element if `size="small"`. */
  labelSmall: string
  /** Styles applied to the label `span` element if `size="medium"`. */
  labelMedium: string
  /** Styles applied to the label `span` element if `size="large"`. */
  labelLarge: string
  /** Styles applied to the deleteIcon element. */
  deleteIcon: string
  /** Styles applied to the deleteIcon element if `size="exSmall"`. */
  deleteIconExSmall: string
  /** Styles applied to the deleteIcon element if `size="small"`. */
  deleteIconSmall: string
  /** Styles applied to the deleteIcon element if `size="medium"`. */
  deleteIconMedium: string
  /** Styles applied to the deleteIcon element if `size="large"`. */
  deleteIconLarge: string
  /** Styles applied to the deleteIcon element if `color="primary"` and `variant="filled"`. */
  deleteIconColorPrimary: string
  /** Styles applied to the deleteIcon element if `color="secondary"` and `variant="filled"`. */
  deleteIconColorSecondary: string
  /** Styles applied to the deleteIcon element if `color="primary"` and `variant="outlined"`. */
  deleteIconOutlinedColorPrimary: string
  /** Styles applied to the deleteIcon element if `color="secondary"` and `variant="outlined"`. */
  deleteIconOutlinedColorSecondary: string
  /** Styles applied to the deleteIcon element if `color="primary"` and `variant="filled"`. */
  deleteIconFilledColorPrimary: string
  /** Styles applied to the deleteIcon element if `color="secondary"` and `variant="filled"`. */
  deleteIconFilledColorSecondary: string
  /** State class applied to the root element if keyboard focused. */
  focusVisible: string
  /** Styles applied to the root element if `borderVariant="circular"`. */
  circular: string
  /** Styles applied to the root element if `borderVariant="rounded"`. */
  rounded: string
  /** Styles applied to the root element if `borderVariant="square"`. */
  square: string
}

export interface ChipProps
  extends Partial<React.HTMLAttributes<HTMLDivElement>> {
  /**
   * The Avatar element to display.
   */
  avatar?: React.ReactElement<AvatarProps>
  /**
   * This prop isn't supported.
   * Use the `component` prop if you need to change the children structure.
   */
  children?: null
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<ChipClasses>
  /**
   * If `true`, the chip will appear clickable, and will raise when pressed,
   * even if the onClick prop is not defined.
   * If `false`, the chip will not appear clickable, even if onClick prop is defined.
   * This can be used, for example,
   * along with the component prop to indicate an anchor Chip is clickable.
   * Note: this controls the UI and does not affect the onClick event.
   */
  clickable?: boolean
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  /**
   * Override the default delete icon element. Shown only if `onDelete` is set.
   */
  deleteIcon?: React.ReactElement<SvgIconProps>
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean
  /**
   * Icon element.
   */
  icon?: React.ReactElement<SvgIconProps>
  /**
   * The content of the component.
   */
  label?: React.ReactNode
  /**
   * Callback fired when the delete icon is clicked.
   * If set, the delete icon will be shown.
   */
  onDelete?: React.EventHandler<any>
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: 'exSmall' | 'small' | 'medium' | 'large'
  /**
   * If `true`, allows the disabled chip to escape focus.
   * If `false`, allows the disabled chip to receive focus.
   * @default false
   */
  focusableWhenDisabled?: boolean
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   *  @ignore
   */
  tabIndex?: number
  /**
   * The variant to use.
   * @default 'filled'
   */
  variant?: 'filled' | 'outlined'
  /**
   * The shape of the chip.
   * @default 'rounded'
   */
  borderVariant?: 'circular' | 'rounded' | 'square'
}

function isDeleteKeyboardEvent(
  keyboardEvent: React.KeyboardEvent<HTMLDivElement>
) {
  return keyboardEvent.key === 'Backspace' || keyboardEvent.key === 'Delete'
}

/**
 * Chips represent complex entities in small blocks, such as a contact.
 */
export const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  function Chip(props, ref) {
    const {
      avatar: avatarProp,
      classes,
      className,
      clickable: clickableProp,
      color = 'primary',
      Component: ComponentProp,
      deleteIcon: deleteIconProp,
      disabled = false,
      icon: iconProp,
      label,
      onClick,
      onDelete,
      onKeyDown,
      onKeyUp,
      size = 'medium',
      variant = 'filled',
      tabIndex,
      focusableWhenDisabled = false,
      borderVariant = 'rounded',
      sx,
      ...other
    } = props

    const chipRef = React.useRef<HTMLDivElement>(null)
    const handleRef = useForkRef(chipRef, ref)

    const handleDeleteIconClick = (
      event: React.MouseEventHandler<HTMLOrSVGElement> | unknown
    ) => {
      // Stop the event from bubbling up to the `Chip`
      ;(event as Event).stopPropagation()
      if (onDelete) {
        onDelete(event)
      }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Ignore events from children of `Chip`.
      if (
        event.currentTarget === event.target &&
        isDeleteKeyboardEvent(event)
      ) {
        // Will be handled in keyUp, otherwise some browsers
        // might init navigation
        event.preventDefault()
      }

      if (onKeyDown) {
        onKeyDown(event)
      }
    }

    const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Ignore events from children of `Chip`.
      if (event.currentTarget === event.target) {
        if (onDelete && isDeleteKeyboardEvent(event)) {
          onDelete(event)
        } else if (event.key === 'Escape' && chipRef.current) {
          chipRef.current.blur()
        }
      }

      if (onKeyUp) {
        onKeyUp(event)
      }
    }

    const clickable = clickableProp !== false && onClick ? true : clickableProp

    const Component =
      clickable || onDelete ? ButtonBase : ComponentProp || 'div'

    const moreProps =
      Component === ButtonBase
        ? {
            component: ComponentProp || 'div',
            focusVisibleClassName: classes?.focusVisible,
            ...(onDelete && { disableRipple: true }),
          }
        : {}

    let deleteIcon = null
    if (onDelete) {
      deleteIcon =
        deleteIconProp && React.isValidElement(deleteIconProp) ? (
          React.cloneElement(deleteIconProp, {
            //@ts-ignore
            className: clsx(
              deleteIconProp.props.className,
              classes?.deleteIcon
            ),
            onClick: handleDeleteIconClick,
          })
        ) : (
          <CloseIcon
            className={clsx(
              styles.deleteIcon,
              [
                //@ts-ignore
                styles[`size-${size}`],
                // @ts-ignore
                classes?.[`deleteIcon${capitalize(size)}`],
              ],
              classes?.deleteIcon
            )}
            onClick={handleDeleteIconClick}
          />
        )
    }

    let avatar = null
    if (avatarProp && React.isValidElement(avatarProp)) {
      avatar = React.cloneElement(avatarProp, {
        // @ts-ignore
        className: clsx(
          styles.avatar,
          classes?.avatar,
          [
            // @ts-ignore
            styles[`size-${size}`],
            // @ts-ignore
            classes?.[`avatar${capitalize(size)}`],
          ],
          // @ts-ignore
          [styles[`color-${color}`], classes?.[`color${capitalize(color)}`]],
          avatarProp.props.className
        ),
      })
    }

    let icon = null
    if (iconProp && React.isValidElement(iconProp)) {
      icon = React.cloneElement(iconProp, {
        //@ts-ignore
        className: clsx(
          styles.icon,
          [
            // @ts-ignore
            styles[`size-${size}`],
            // @ts-ignore
            classes?.[`size${capitalize(size)}`],
          ],
          classes?.icon,
          iconProp.props.className
        ),
      })
    }

    if (process.env.NODE_ENV !== 'production') {
      if (avatar && icon) {
        console.error(
          'The Chip component can not handle the avatar ' +
            'and the icon prop at the same time. Pick one.'
        )
      }
    }

    const _classes = clsx(
      styles.root,
      disabled && styles.disabled,
      [
        // @ts-ignore
        styles[`size-${size}`],
        // @ts-ignore
        classes?.[`size${capitalize(size)}`],
      ],
      clickable && [styles.clickable, classes?.clickable],
      [styles[`variant-${variant}`], classes?.[variant]],
      // @ts-ignore
      [styles[`color-${color}`], classes?.[`color${capitalize(color)}`]],
      classes?.root,
      borderVariant && styles[`${borderVariant}`],
      className,
      sxFunc(sx)
    )

    return (
      <Component
        className={_classes}
        disabled={clickable && disabled ? true : undefined}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        ref={handleRef}
        tabIndex={focusableWhenDisabled && disabled ? -1 : tabIndex}
        {...moreProps}
        {...other}
      >
        {avatar || icon}
        <span
          className={clsx(
            styles.label,
            size === 'small' && [
              styles[`size-${size}`],
              // @ts-ignore
              classes?.[`label${capitalize(size)}`],
            ],
            classes?.label
          )}
        >
          {label}
        </span>
        {deleteIcon}
      </Component>
    )
  }
)
